import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

/**
 * BALDER — an interactive intro assistant for the marketing front page.
 *
 * This is a deliberately SCRIPTED preview: it makes no API calls and holds no
 * live data. It exists to introduce who BALDER is and what he can do, using a
 * small set of canned questions and honest answers. The panel is clearly
 * labelled "Preview" so no visitor mistakes it for the real, connected assistant.
 *
 * TRUTHFULNESS: every answer below matches what ODIN actually does — BALDER
 * advises, drafts blueprints and produces dry-run previews, but never deploys or
 * approves changes on its own; live deployment is locked by default; the free
 * plan is genuinely €0. No fabricated metrics, testimonials or guarantees.
 */

type Sender = "balder" | "user";
interface Message {
  id: number;
  from: Sender;
  text: string;
}

interface Prompt {
  key: string;
  label: string;
  answer: string;
}

const GREETING =
  "Hi, I’m BALDER — ODIN’s built-in AI assistant. I help you design and review changes to your Discord server in plain language, before anything goes live. Ask me anything to get a feel for it.";

const PROMPTS: ReadonlyArray<Prompt> = [
  {
    key: "who",
    label: "Who are you?",
    answer:
      "I’m BALDER, the assistant built into ODIN. Think of me as a careful co-pilot for your community: I can look at how your server is set up, suggest improvements, and explain what each change would do — always in plain language, never jargon.",
  },
  {
    key: "what",
    label: "What can you do?",
    answer:
      "I can inspect your server, draft blueprint changes to roles, channels, onboarding and moderation, and produce a dry-run preview of exactly what would be created, changed or removed. I explain every step — but I never deploy or approve anything on my own. You always have the final say.",
  },
  {
    key: "safe",
    label: "Is it safe?",
    answer:
      "Very. Nothing structural reaches your server until you review a dry run and approve it, and live deployment stays locked by default. Every release is snapshotted so you can roll it back. Your bot token is encrypted and never shown back to you — not even in the dashboard.",
  },
  {
    key: "start",
    label: "How do I start?",
    answer:
      "Create a free account, connect your own Discord bot, and I’ll help you prepare your first change — previewed before anything goes live. The Free plan is €0 with no credit card, so you can explore everything at your own pace.",
  },
];

/** BALDER mark (winged-profile emblem), used in the launcher, header and messages. */
function BalderMark({ className }: { className?: string }): JSX.Element {
  return (
    <span className={`balder-mark${className ? ` ${className}` : ""}`} aria-hidden="true">
      <img src="/brand/balder/balder-mark-128.png" alt="" className="balder-mark__img" />
    </span>
  );
}

export function BalderAssistant(): JSX.Element {
  const [open, setOpen] = useState(false);
  const [autoOpened, setAutoOpened] = useState(false);
  const [messages, setMessages] = useState<ReadonlyArray<Message>>([
    { id: 0, from: "balder", text: GREETING },
  ]);
  const [asked, setAsked] = useState<ReadonlySet<string>>(new Set());
  const [typing, setTyping] = useState(false);
  const nextId = useRef(1);
  const threadRef = useRef<HTMLDivElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);

  // Gently auto-open once, shortly after the page settles, to invite interaction.
  useEffect(() => {
    if (autoOpened) return;
    const t = window.setTimeout(() => {
      setOpen(true);
      setAutoOpened(true);
    }, 2600);
    return () => window.clearTimeout(t);
  }, [autoOpened]);

  // Keep the newest message in view.
  useEffect(() => {
    const el = threadRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, typing, open]);

  // Escape closes the panel.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  const ask = (prompt: Prompt): void => {
    if (typing) return;
    setAsked((prev) => new Set(prev).add(prompt.key));
    setMessages((prev) => [
      ...prev,
      { id: nextId.current++, from: "user", text: prompt.label },
    ]);
    setTyping(true);
    window.setTimeout(() => {
      setMessages((prev) => [
        ...prev,
        { id: nextId.current++, from: "balder", text: prompt.answer },
      ]);
      setTyping(false);
    }, 650);
  };

  const remaining = PROMPTS.filter((p) => !asked.has(p.key));

  return (
    <div className="balder">
      {open ? (
        <div
          className="balder-panel"
          role="dialog"
          aria-label="BALDER — ODIN assistant preview"
          ref={panelRef}
        >
          <header className="balder-panel__head">
            <BalderMark className="balder-mark--head" />
            <div className="balder-panel__id">
              <span className="balder-panel__name">BALDER</span>
              <span className="balder-panel__role">AI assistant · Preview</span>
            </div>
            <button
              type="button"
              className="balder-panel__close"
              aria-label="Close assistant"
              onClick={() => setOpen(false)}
            >
              <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M5 5l10 10M15 5L5 15" strokeLinecap="round" />
              </svg>
            </button>
          </header>

          <div className="balder-thread" ref={threadRef}>
            {messages.map((m) => (
              <div key={m.id} className={`balder-msg balder-msg--${m.from}`}>
                {m.from === "balder" ? <BalderMark className="balder-msg__mark" /> : null}
                <p className="balder-bubble">{m.text}</p>
              </div>
            ))}
            {typing ? (
              <div className="balder-msg balder-msg--balder">
                <BalderMark className="balder-msg__mark" />
                <p className="balder-bubble balder-bubble--typing" aria-label="BALDER is typing">
                  <span />
                  <span />
                  <span />
                </p>
              </div>
            ) : null}
          </div>

          {remaining.length > 0 ? (
            <div className="balder-prompts">
              {remaining.map((p) => (
                <button
                  key={p.key}
                  type="button"
                  className="balder-chip"
                  onClick={() => ask(p)}
                  disabled={typing}
                >
                  {p.label}
                </button>
              ))}
            </div>
          ) : null}

          <footer className="balder-panel__foot">
            <span className="balder-panel__note">Scripted preview — the real BALDER lives in the app.</span>
            <Link to="/login" className="balder-panel__cta">
              Start free
            </Link>
          </footer>
        </div>
      ) : null}

      <button
        type="button"
        className={`balder-launcher${open ? " is-open" : ""}`}
        aria-expanded={open}
        aria-label={open ? "Close BALDER assistant" : "Chat with BALDER"}
        onClick={() => setOpen((v) => !v)}
      >
        <BalderMark className="balder-mark--launcher" />
        {!open ? <span className="balder-launcher__label">Meet BALDER</span> : null}
      </button>
    </div>
  );
}
